import { Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ScheduleDemo from './pages/ScheduleDemo';
import AppShell from './components/AppShell';
import { ProtectedRoute } from './routes/ProtectedRoute';
import DashboardPage from './pages/DashboardPage';
import ItemsPage from './pages/items/ItemsPage';
import SuppliersPage from './pages/suppliers/SuppliersPage';
import PurchaseRequestsPage from './pages/purchasing/PurchaseRequestsPage';
import PurchaseOrdersPage from './pages/purchasing/PurchaseOrdersPage';
import GoodsReceiptsPage from './pages/purchasing/GoodsReceiptsPage';
import StockLedgerPage from './pages/inventory/StockLedgerPage';
import StockBalancePage from './pages/inventory/StockBalancePage';
import StockOpnamePage from './pages/inventory/StockOpnamePage';
import TransfersPage from './pages/inventory/TransfersPage';
import WastePage from './pages/inventory/WastePage';
import ExpiryLotsPage from './pages/inventory/ExpiryLotsPage';
import MenuMasterPage from './pages/production/MenuMasterPage';
import RecipesPage from './pages/production/RecipesPage';
import ProductionPage from './pages/production/ProductionPage';
import EmployeesPage from './pages/hr/EmployeesPage';
import AttendancePage from './pages/hr/AttendancePage';
import LeavePage from './pages/hr/LeavePage';
import ShiftSwapsPage from './pages/hr/ShiftSwapsPage';
import DisciplinaryPage from './pages/hr/DisciplinaryPage';
import ShiftsPage from './pages/hr/ShiftsPage';
import SchedulePage from './pages/hr/SchedulePage';
import AssetsPage from './pages/assets/AssetsPage';
import MaintenancePage from './pages/maintenance/MaintenancePage';
import CrmPage from './pages/crm/CrmPage';
import CommandCenterPage from './pages/reporting/CommandCenterPage';
import ReportCenterPage from './pages/reporting/ReportCenterPage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import AiAnalystPage from './pages/ai/AiAnalystPage';
import MigrationDashboardPage from './pages/migration/MigrationDashboardPage';
import ChartOfAccountsPage from './pages/finance/ChartOfAccountsPage';
import PayrollPage from './pages/finance/PayrollPage';
import ApPage from './pages/finance/ApPage';
import JournalsPage from './pages/finance/JournalsPage';
import FinancialReportsPage from './pages/finance/FinancialReportsPage';
import ModulePlaceholder from './components/ModulePlaceholder';

/**
 * ALEX ERP web — route tree for Vertical-Slice-1.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED (see docs/data-dictionary.md §8k). This is
 * the real route structure for the AUDITOR DIRECTION vertical slice: Login
 * -> Master Item -> Supplier -> Create PR -> Approval -> Create PO -> Goods
 * Receipt -> Stock Ledger -> Stock Balance, for TENANT_OWNER / MANAGER /
 * PURCHASING / WAREHOUSE / AUDITOR (server-side read-only via migration
 * 0032's permission grants — AUDITOR simply never gets a `.create`/
 * `.approve`/`.post` permission, so every mutating control on every page
 * below is already hidden for that role without any AUDITOR-specific
 * branching in this file).
 *
 * Every page beyond this slice's 8 screens still routes to
 * `ModulePlaceholder` — this file does not pretend more of the app exists
 * than what AUDITOR DIRECTION asked to be built first. The rest of
 * `web-preview/index.html`'s pages remain the DEV_TESTED evidence surface
 * for their respective backend modules until later slices migrate them here
 * too (AUDITOR DIRECTION item 5).
 */
export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/lupa-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/jadwalkan-demo" element={<ScheduleDemo />} />
      <Route path="/" element={<Navigate to="/app" replace />} />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route
          path="items"
          element={
            <ProtectedRoute requirePermission={{ resource: 'item', action: 'read' }}>
              <ItemsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="suppliers"
          element={
            <ProtectedRoute requirePermission={{ resource: 'supplier', action: 'read' }}>
              <SuppliersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="purchase-requests"
          element={
            <ProtectedRoute requirePermission={{ resource: 'purchase_request', action: 'read' }}>
              <PurchaseRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="purchase-orders"
          element={
            <ProtectedRoute requirePermission={{ resource: 'purchase_order', action: 'read' }}>
              <PurchaseOrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="goods-receipts"
          element={
            <ProtectedRoute requirePermission={{ resource: 'goods_receipt', action: 'read' }}>
              <GoodsReceiptsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="stock-ledger"
          element={
            <ProtectedRoute requirePermission={{ resource: 'stock_ledger', action: 'read' }}>
              <StockLedgerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="stock-balance"
          element={
            <ProtectedRoute requirePermission={{ resource: 'stock_balance', action: 'read' }}>
              <StockBalancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="stock-opname"
          element={
            <ProtectedRoute requirePermission={{ resource: 'stock_count', action: 'read' }}>
              <StockOpnamePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="transfers"
          element={
            <ProtectedRoute requirePermission={{ resource: 'transfer', action: 'read' }}>
              <TransfersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="waste"
          element={
            <ProtectedRoute requirePermission={{ resource: 'waste', action: 'read' }}>
              <WastePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="expiry"
          element={
            <ProtectedRoute requirePermission={{ resource: 'expiry_lot', action: 'read' }}>
              <ExpiryLotsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="menus"
          element={
            <ProtectedRoute requirePermission={{ resource: 'menu', action: 'read' }}>
              <MenuMasterPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="recipes"
          element={
            <ProtectedRoute requirePermission={{ resource: 'recipe', action: 'read' }}>
              <RecipesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="production"
          element={
            <ProtectedRoute requirePermission={{ resource: 'production_batch', action: 'read' }}>
              <ProductionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="employees"
          element={
            <ProtectedRoute requirePermission={{ resource: 'employee', action: 'read' }}>
              <EmployeesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="attendance"
          element={
            <ProtectedRoute requirePermission={{ resource: 'attendance_event', action: 'read' }}>
              <AttendancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="shifts"
          element={
            <ProtectedRoute requirePermission={{ resource: 'shift', action: 'read' }}>
              <ShiftsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="schedule"
          element={
            <ProtectedRoute requirePermission={{ resource: 'schedule', action: 'read' }}>
              <SchedulePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="leave"
          element={
            <ProtectedRoute requirePermission={{ resource: 'leave_request', action: 'read' }}>
              <LeavePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="shift-swaps"
          element={
            <ProtectedRoute requirePermission={{ resource: 'shift_swap', action: 'read' }}>
              <ShiftSwapsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="disciplinary"
          element={
            <ProtectedRoute requirePermission={{ resource: 'disciplinary_action', action: 'read' }}>
              <DisciplinaryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="finance/coa"
          element={
            <ProtectedRoute requirePermission={{ resource: 'chart_of_account', action: 'read' }}>
              <ChartOfAccountsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="finance/payroll"
          element={
            <ProtectedRoute requirePermission={{ resource: 'payroll_period', action: 'read' }}>
              <PayrollPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="finance/ap"
          element={
            <ProtectedRoute requirePermission={{ resource: 'ap_invoice', action: 'read' }}>
              <ApPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="finance/journals"
          element={
            <ProtectedRoute requirePermission={{ resource: 'journal_entry', action: 'read' }}>
              <JournalsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="finance/reports"
          element={
            <ProtectedRoute requirePermission={{ resource: 'journal_entry', action: 'read' }}>
              <FinancialReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="assets"
          element={
            <ProtectedRoute requirePermission={{ resource: 'asset', action: 'read' }}>
              <AssetsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="maintenance"
          element={
            <ProtectedRoute requirePermission={{ resource: 'maintenance_order', action: 'read' }}>
              <MaintenancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="crm"
          element={
            <ProtectedRoute requirePermission={{ resource: 'customer', action: 'read' }}>
              <CrmPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="command-center"
          element={
            <ProtectedRoute requirePermission={{ resource: 'report', action: 'read' }}>
              <CommandCenterPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="reports"
          element={
            <ProtectedRoute requirePermission={{ resource: 'report', action: 'read' }}>
              <ReportCenterPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="notifications"
          element={
            <ProtectedRoute requirePermission={{ resource: 'notification', action: 'read' }}>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="ai"
          element={
            <ProtectedRoute requirePermission={{ resource: 'ai', action: 'query' }}>
              <AiAnalystPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="migration"
          element={
            <ProtectedRoute requirePermission={{ resource: 'migration', action: 'read' }}>
              <MigrationDashboardPage />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route
        path="*"
        element={
          <ModulePlaceholder
            title="Belum dimigrasikan ke apps/web"
            status="DEFERRED"
            description="Halaman ini masih REAL di web-preview/index.html sebagai evidence backend; migrasi ke sini menyusul di slice berikutnya (AUDITOR DIRECTION item 5)."
          />
        }
      />
    </Routes>
  );
}
